import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, Calendar, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { updateJob } from "@/api/engine";
import {
  CronSchedulerBuilder,
  type CronSchedulerValue,
} from "./CronSchedulerBuilder";
import { useDefaultTimezone } from "@/hooks/useDefaultTimezone";
import type { JobConfig } from "@/api/types";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  cron_expression: z.string().min(1, "Cron expression is required"),
  timezone: z.string().min(1),
  is_enabled: z.boolean(),
  has_window: z.boolean(),
  starts_at_date: z.string(),
  ends_at_date: z.string(),
  config_json: z
    .string()
    .refine(
      (v) => {
        const trimmed = v.trim();
        if (trimmed === "") return true;
        try {
          const parsed = JSON.parse(trimmed);
          return parsed !== null && typeof parsed === "object";
        } catch {
          return false;
        }
      },
      { message: "Must be a valid JSON object" },
    ),
});

type FormValues = z.infer<typeof schema>;

interface ConfigModalProps {
  job: JobConfig | null;
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (updated: JobConfig) => void;
}

export function ConfigModal({
  job,
  projectSlug,
  open,
  onOpenChange,
  onSuccess,
}: ConfigModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [cronValid, setCronValid] = useState(true);
  // Browser-resolved (or user-preferred) timezone — used as the
  // fallback when a job has no timezone configured yet. Edits of
  // jobs that DO have a timezone always seed from job.timezone.
  const defaultTimezone = useDefaultTimezone();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      cron_expression: "",
      timezone: defaultTimezone,
      is_enabled: true,
      has_window: false,
      starts_at_date: "",
      ends_at_date: "",
      config_json: "{}",
    },
  });

  useEffect(() => {
    if (open && job) {
      const startsDate = isoToDateString(job.starts_at);
      const endsDate = isoToDateString(job.ends_at);
      reset({
        name: job.name,
        cron_expression: job.cron_expression,
        timezone: job.timezone || defaultTimezone,
        is_enabled: job.is_enabled,
        has_window: job.starts_at !== null || job.ends_at !== null,
        starts_at_date: startsDate ?? "",
        ends_at_date: endsDate ?? "",
        config_json: JSON.stringify(job.config ?? {}, null, 2),
      });
      setCronValid(true);
    }
  }, [open, job, reset]);

  const isEnabled = watch("is_enabled");
  const hasWindow = watch("has_window");
  const startsDate = watch("starts_at_date");
  const endsDate = watch("ends_at_date");

  const windowValidation = useMemo(() => {
    if (!hasWindow)
      return { error: null as string | null, warning: null as string | null };
    let error: string | null = null;
    let warning: string | null = null;
    if (startsDate && endsDate && startsDate >= endsDate) {
      error = "Start date must be before end date";
    }
    if (!error && endsDate) {
      const todayStr = new Date().toISOString().split("T")[0];
      if (endsDate < todayStr) {
        warning = "End date is in the past, the job won't run anymore";
      }
    }
    return { error, warning };
  }, [hasWindow, startsDate, endsDate]);

  const handleScheduleChange = useCallback(
    (next: CronSchedulerValue) => {
      setValue("cron_expression", next.cron, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("timezone", next.timezone, {
        shouldDirty: true,
      });
      setCronValid(next.valid);
    },
    [setValue],
  );

  const previewStartsAt =
    hasWindow && startsDate ? dateToStartIso(startsDate) : null;
  const previewEndsAt =
    hasWindow && endsDate ? dateToEndIso(endsDate) : null;

  async function onSubmit(values: FormValues) {
    if (!job) return;
    if (windowValidation.error) {
      toast.error(windowValidation.error);
      return;
    }
    setSubmitting(true);
    try {
      const cfgString = values.config_json.trim();
      const cfg =
        cfgString === ""
          ? {}
          : (JSON.parse(cfgString) as Record<string, unknown>);

      const startsAt =
        values.has_window && values.starts_at_date
          ? dateToStartIso(values.starts_at_date)
          : null;
      const endsAt =
        values.has_window && values.ends_at_date
          ? dateToEndIso(values.ends_at_date)
          : null;

      const updated = await updateJob(projectSlug, job.job_id, {
        name: values.name,
        cron_expression: values.cron_expression,
        timezone: values.timezone,
        is_enabled: values.is_enabled,
        starts_at: startsAt,
        ends_at: endsAt,
        config: cfg,
      });
      toast.success(`${updated.name} updated`);
      onSuccess(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="flex max-h-[90vh] max-w-[640px] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Job configuration</DialogTitle>
          <DialogDescription>
            {job ? (
              <>
                Editing <span className="font-medium text-slate-700">{job.name}</span>
                <code className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">
                  {job.job_id}
                </code>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {job && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
            noValidate
          >
            <div className="-mx-1 flex-1 space-y-5 overflow-y-auto px-1 py-1">
              <Field label="Name" error={errors.name?.message}>
                <Input
                  {...register("name")}
                  className="h-10 rounded-lg border-slate-200"
                />
              </Field>

              <CronSchedulerBuilder
                key={job.job_id}
                cron={job.cron_expression}
                timezone={job.timezone || defaultTimezone}
                onChange={handleScheduleChange}
                startsAt={previewStartsAt}
                endsAt={previewEndsAt}
              />

              {/* Scheduling window */}
              <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        Set a window
                      </div>
                      <p className="text-xs text-slate-500">
                        Restrict the job to a date range
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={hasWindow}
                    onCheckedChange={(v) =>
                      setValue("has_window", v, { shouldDirty: true })
                    }
                  />
                </div>

                {hasWindow && (
                  <div className="space-y-3 border-t border-slate-900/[0.06] pt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Field
                        label="Start date"
                        hint="Job won't run before this date"
                      >
                        <Input
                          type="date"
                          {...register("starts_at_date")}
                          className="h-9 rounded-lg border-slate-200"
                        />
                      </Field>
                      <Field
                        label="End date"
                        hint="Job stops automatically after this date"
                      >
                        <Input
                          type="date"
                          {...register("ends_at_date")}
                          className="h-9 rounded-lg border-slate-200"
                        />
                      </Field>
                    </div>
                    {windowValidation.error && (
                      <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{windowValidation.error}</span>
                      </div>
                    )}
                    {windowValidation.warning && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{windowValidation.warning}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Enabled
                  </div>
                  <p className="text-xs text-slate-500">
                    Disable to skip scheduled runs (manual triggers still work)
                  </p>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(v) =>
                    setValue("is_enabled", v, { shouldDirty: true })
                  }
                />
              </div>

              <Field
                label="Config (JSON)"
                error={errors.config_json?.message}
                hint="Job-specific parameters"
              >
                <Textarea
                  {...register("config_json")}
                  spellCheck={false}
                  className="min-h-[120px] rounded-lg border-slate-200 font-mono text-xs"
                  placeholder="{}"
                />
              </Field>
            </div>

            <DialogFooter className="border-t border-slate-900/[0.06] pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting ||
                  !isDirty ||
                  !cronValid ||
                  windowValidation.error !== null
                }
              variant="primary"
              >
                {submitting && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

function isoToDateString(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateToStartIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

function dateToEndIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save the job.";
}
